import * as React from 'react'

import type Employee from 'modules/employees/store/models/Employee'
import useBuildPermissions from 'modules/payroll_bundle/components/Supplements/useBuildPermissions'
import type PolicyPeriod from 'modules/payroll_bundle/store/models/PolicyPeriod'

import type { PolicyPeriodAuthorizations } from './types'

type SupplementPermissions = ReturnType<typeof useBuildPermissions>['supplementPermissions'][number]

interface StatusContextType {
  loading: boolean
  policyPeriodAuthorizations: PolicyPeriodAuthorizations
  supplementPermissions: Record<string, SupplementPermissions>
}

const DEFAULT_VALUE = {
  loading: false,
  supplementPermissions: {},
  policyPeriodAuthorizations: {
    edit_observations: false,
    edit_supplements: false,
    validate_supplements: false,
  },
}

const StatusContext = React.createContext<StatusContextType | null | undefined>(null)

export const StatusAuthorizationProvider = ({
  children,
  policyPeriod,
  policyPeriodStatus,
  employees,
}: {
  children: React.ReactNode
  policyPeriod?: PolicyPeriod | null
  policyPeriodStatus?: string
  employees: Employee[]
}) => {
  const value = useBuildPermissions({
    policyPeriod,
    employees,
    policyPeriodStatus,
  })
  return (
    <StatusContext.Provider value={employees.length ? value : DEFAULT_VALUE}>
      {children}
    </StatusContext.Provider>
  )
}

export const useStatusAuthorizations = (): StatusContextType => {
  const statusAuthorizations = React.useContext(StatusContext)

  if (!statusAuthorizations) {
    throw new Error('useStatusAuthorizations must be used within a StatusAuthorizationProvider')
  }

  return statusAuthorizations
}
